import React, { useState } from "react";
import {
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
  FaYoutube,
  FaLinkedinIn
} from "react-icons/fa6";
import {
  IoMdMail,
  IoMdCall
} from "react-icons/io";
import {
  MdLocationOn,
  MdArrowForward,
  MdOutlineMarkEmailRead
} from "react-icons/md";
import { useTenant } from "../Context/TenantContext";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "./UserContext";

const Footer = () => {
  const navigate = useNavigate();
  const { user } = React.useContext(UserContext);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const {
    getLogoUrl,
    getCollegeName,
    getCollegePhone,
    getCollegeEmail,
    getCollegeLocation,
    tenant
  } = useTenant();

  const isMainPlatform = !tenant ||
    tenant.domain === 'theskillgenie.com' ||
    tenant.tenantKey === 'theskillgenie' ||
    window.location.hostname === 'theskillgenie.com' ||
    window.location.hostname === 'www.theskillgenie.com' ||
    window.location.hostname === 'localhost';

  const phone = getCollegePhone() || "+91 62815 97586";
  const email = getCollegeEmail() || "connect@evolvexinnovations.org";
  const location = getCollegeLocation() || "Bhoomatha Thalluri Heights, Kankipadu, Vijayawada – 521151";

  const protectedLinks = [
    "Elite Mastery Technologies",
    "Mock Interviews",
    "AI Tutor",
    "Compiler",
    "Documentation"
  ];

  const handleLinkClick = (e, link) => {
    if (protectedLinks.includes(link.name) && !user) {
      e.preventDefault();
      navigate("/login");
    }
  };

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    // No newsletter backend yet — route the signup through the support inbox.
    window.location.href = `mailto:${email}?subject=${encodeURIComponent("Newsletter Subscription")}&body=${encodeURIComponent(`Please subscribe ${newsletterEmail} to The Skill Genie updates.`)}`;
    setSubscribed(true);
  };

  const footerLinks = [
    {
      title: "Company",
      links: [
        { name: "For Colleges", path: "/college-intro" },
        { name: "Book a Demo", path: "/book-demo" }
      ]
    },
    {
      title: "Features",
      links: [
        { name: "Elite Mastery Technologies", path: "/techgrid", noLink: true },
        { name: "Mock Interviews", path: "/mockinterviewhome", noLink: true },
        { name: "AI Tutor", path: "/tsg", noLink: true }
      ]
    },
    {
      title: "Support",
      links: [
        { name: "Contact Us", path: "/contact" }
      ]
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", path: "/privacy-policy" },
        { name: "Terms of Service", path: "/terms-of-service" }
      ]
    }
  ];

  return (
    <footer className="bg-[#0F172A] text-white pt-20 pb-12 px-6 md:px-12 overflow-hidden relative font-sans">
      {/* Background decoration */}
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] bg-purple-600/10 rounded-full blur-[110px] pointer-events-none"></div>
      <div className="absolute top-0 left-0 w-[320px] h-[320px] bg-indigo-600/10 rounded-full blur-[110px] pointer-events-none"></div>

      <div className="max-w-[1400px] mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-4 space-y-7">
            {isMainPlatform ? (
              /* The TSG lockup already carries the name + tagline — render it on a
                 white card so the dark logo stays crisp on the dark footer. */
              <div className="inline-block bg-white rounded-2xl p-3 shadow-lg border border-white/20">
                <img
                  src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
                  alt="TSG — The Skill Genie. Empowering Future Builders. Engineering Impact."
                  height="56"
                  loading="lazy"
                  decoding="async"
                  className="h-12 md:h-14 w-auto object-contain"
                  onError={(e) => { e.target.onerror = null; e.target.src = "/logo192.png"; }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-3.5">
                <img
                  src={getLogoUrl()}
                  alt={`${getCollegeName()} logo`}
                  width="56"
                  height="56"
                  loading="lazy"
                  decoding="async"
                  className="w-14 h-14 rounded-2xl bg-white p-1 object-contain border border-white/20 shadow-lg"
                  onError={(e) => { e.target.onerror = null; e.target.src = "/logo192.png"; }}
                />
                <span className="text-2xl font-black tracking-tight text-white block leading-none">
                  {getCollegeName()}
                </span>
              </div>
            )}

            <p className="text-purple-100/90 text-base leading-relaxed max-w-md">
              {isMainPlatform ?
                "Build Skills. Build Products. Build Careers. The AI-powered Industry Readiness Platform transforming learning into real-world engineering excellence." :
                `Empowering ${getCollegeName()} students with industry-ready skills through our AI-driven learning platform, ensuring global career success.`
              }
            </p>

            {/* Newsletter */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-[2px] text-white/60 mb-3">Stay Updated</h4>
              {subscribed ? (
                <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm">
                  <MdOutlineMarkEmailRead size={20} /> Thanks — you're on the list!
                </div>
              ) : (
                <form onSubmit={handleNewsletter} className="flex gap-2 max-w-sm">
                  <label htmlFor="footer-newsletter" className="sr-only">Email address</label>
                  <input
                    id="footer-newsletter"
                    type="email"
                    required
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white/[0.06] border border-white/15 text-sm text-white placeholder-white/40 focus:outline-none focus:border-purple-400 focus:bg-white/[0.09] transition-all"
                  />
                  <button
                    type="submit"
                    className="px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-bold hover:shadow-lg hover:shadow-purple-900/40 transition-all flex-shrink-0"
                  >
                    Subscribe
                  </button>
                </form>
              )}
            </div>

            <div className="flex gap-3">
              {[
                { icon: <FaFacebookF />, url: "https://facebook.com/theskillgenie", label: "Facebook" },
                { icon: <FaXTwitter />, url: "https://x.com/EvolveX2025", label: "X (Twitter)" },
                { icon: <FaInstagram />, url: "https://www.instagram.com/theskillgenie_", label: "Instagram" },
                { icon: <FaYoutube />, url: "https://www.youtube.com/@EvolveXInnovations", label: "YouTube" }
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-[#843FEF] hover:border-white hover:-translate-y-0.5 transition-all duration-300"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {footerLinks.map((section, idx) => (
              <nav key={idx} className="space-y-5" aria-label={`Footer — ${section.title}`}>
                <h4 className="text-sm font-bold uppercase tracking-[2px] text-white/60">{section.title}</h4>
                <ul className="space-y-3.5 list-none p-0 m-0">
                  {section.links.map((link, i) => (
                    <li key={i}>
                      {link.noLink ? (
                        <span className="text-purple-100 font-medium text-[15px]">{link.name}</span>
                      ) : (
                        <Link
                          to={link.path}
                          onClick={(e) => handleLinkClick(e, link)}
                          className="text-purple-100 hover:text-white transition-colors flex items-center group font-medium text-[15px]"
                        >
                          <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-white">
                            <MdArrowForward className="h-3 w-3" aria-hidden="true" />
                          </span>
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        {/* Contact Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-10 border-y border-white/10 mb-10">
          <div className="flex items-center gap-4 group">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#843FEF] transition-all border border-white/20 shadow-sm flex-shrink-0">
              <IoMdCall size={24} aria-hidden="true" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-wider m-0">Call Support</p>
              <a href={`tel:${phone}`} className="text-lg font-bold text-white hover:text-white/80 transition-colors">{phone}</a>
            </div>
          </div>

          <div className="flex items-center gap-4 group">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#843FEF] transition-all border border-white/20 shadow-sm flex-shrink-0">
              <IoMdMail size={24} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-wider m-0">Email Us</p>
              <a href={`mailto:${email}`} className="text-lg font-bold text-white hover:text-white/80 transition-colors break-all">{email}</a>
            </div>
          </div>

          <div className="flex items-center gap-4 group">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#843FEF] transition-all border border-white/20 shadow-sm flex-shrink-0">
              <MdLocationOn size={24} aria-hidden="true" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-wider m-0">Visit Us</p>
              <span className="text-sm font-bold text-white leading-tight block">{location}</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-purple-100 text-sm m-0">
            © {new Date().getFullYear()} <span className="text-white font-bold tracking-tighter">{isMainPlatform ? "The Skill Genie" : getCollegeName()}</span>. All rights reserved.
          </p>

          <div className="flex items-center gap-2 text-purple-100 text-sm">
            Made with <span className="text-white animate-pulse" aria-hidden="true">❤</span> by <a href="https://evolvexinnovations.org" target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/80 transition-colors font-bold">EvolveX Innovations</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
